import { SheetObject } from "@/object-cache";
import { useAppSelector } from "@/store/store-hooks";
import {
  FloorPlanRendererBase,
  selectIElementWorldTransform,
} from "@faro-lotv/app-component-toolbox";
import { useIsDarkScheme } from "@faro-lotv/flat-ui";
import {
  IElementGenericImgSheet,
  IElementImgSheetTiled,
  isIElementOverviewImage,
} from "@faro-lotv/ielement-types";
import { FloorPlanBackgroundTransparency, LodFloorPlan } from "@faro-lotv/lotv";
import { EventHandlers } from "@react-three/fiber/dist/declarations/src/core/events";
import { useEffect, type JSX } from "react";
import { Mesh, Side } from "three";

type SimpleSheetRendererProps = EventHandlers & {
  /** The mesh with the floor plan as a texture */
  mesh: Mesh;
  /** The IElement descriptor for this floor plan */
  iElement: IElementGenericImgSheet;
};

/**
 * @returns Renderer for an not Lod ImgSheet
 */
function SimpleSheetRenderer({
  mesh,
  iElement,
  ...events
}: SimpleSheetRendererProps): JSX.Element {
  const DEFAULT_MAP_SIZE = 100;
  const mapWidth = iElement.size?.x ?? DEFAULT_MAP_SIZE;
  const mapHeight = iElement.size?.z ?? DEFAULT_MAP_SIZE;

  const globalPose = useAppSelector(selectIElementWorldTransform(iElement.id));

  return (
    <group {...globalPose} {...events}>
      {/* Translating the mesh so that the position corresponds to the sheet center. The translation is in the XY plane
          because the rotation to the Y-up system is taken care of by 'globalPose' */}
      <primitive
        object={mesh}
        position={[mapWidth / 2, mapHeight / 2, 0]}
        name={iElement.id}
      />
    </group>
  );
}

type LodSheetRendererProps = EventHandlers & {
  /** The LodFloorPlan to render for this floor plan */
  lod: LodFloorPlan;

  /** The IElement that describe this floor plan */
  iElement: IElementImgSheetTiled;
};

/**
 * Determines whether the tiled image sheet includes a low-resolution fallback image
 * for compatibility with clients that don't support tiled rendering.
 *
 * @param imgSheetTiled The tiled image sheet to check.
 * @returns True if a low-resolution fallback image is available, false otherwise.
 */
export function isBackwardCompatibleImgTiledSheet(
  imgSheetTiled: IElementImgSheetTiled,
): boolean {
  return (
    !!imgSheetTiled.metaDataMap?.lowResImage &&
    // This very exact check is part of a migration strategy, and should be removed eventually
    // see https://faro01.atlassian.net/browse/SWEB-8425
    imgSheetTiled.metaDataMap.lowResImage.relativePoseToSheet.pos === null
  );
}

/**
 * @returns the floor plan in the correct position
 */
function LodSheetRenderer({
  lod,
  iElement,
  ...events
}: LodSheetRendererProps): JSX.Element {
  const globalPose = useAppSelector(selectIElementWorldTransform(iElement.id));

  return (
    <group {...globalPose} {...events}>
      <FloorPlanRendererBase
        floorPlan={lod}
        minPixelSize={iElement.pixelWidth / 2}
        isBackwardCompatible={isBackwardCompatibleImgTiledSheet(iElement)}
      />
    </group>
  );
}

export type SheetRendererProps = EventHandlers & {
  sheet: SheetObject;

  /** Override the default transparency. */
  transparent?: boolean;

  /** Override the default side culling. */
  side?: Side;

  /** Override the default background transparency. */
  backgroundTransparent?: FloorPlanBackgroundTransparency;

  /**
   * Enable dark mode compatibility:
   * Invert the lightness and slightly desaturate for improved readability.
   *
   * @default true in dark mode
   */
  dark?: boolean;

  /** When the SheetRenderer is populated from a list of sheets, this is the index of the sheet in the list */
  index?: number;
};

// Offset up the overview maps 20mm so they won't be blocked by CAD or Points aligned at the same position
const OVERVIEW_OFFSET = 0.02;
// Offset up the floor plans 10mm higher, so they will be more prominent than overview maps
const FLOORPLAN_OFFSET = OVERVIEW_OFFSET + 0.01;

// To avoid Z-fighting for sheets at same offset, adjust each sheet using a small increment of 0.1mm
const OFFSET_INCREMENT = 0.0001;

/** Saturation multiplier for inverted sheets, to tone down bright colors on the dark canvas */
const INVERTED_SHEET_SATURATION = 0.6;

/**
 * @returns the floor plan in the correct position
 */
export function SheetRenderer({
  sheet,
  side,
  transparent,
  backgroundTransparent,
  dark,
  index = 0,
  ...events
}: SheetRendererProps): JSX.Element {
  const isDarkMode = useIsDarkScheme();
  const shouldInvertLightness = dark ?? isDarkMode;

  useEffect(() => {
    const oldOffset = sheet.material.altitudeOffset;
    if (isIElementOverviewImage(sheet.iElement)) {
      sheet.material.altitudeOffset =
        OVERVIEW_OFFSET + index * OFFSET_INCREMENT;
    } else {
      sheet.material.altitudeOffset =
        FLOORPLAN_OFFSET + index * OFFSET_INCREMENT;
    }
    return () => {
      sheet.material.altitudeOffset = oldOffset;
    };
  }, [index, sheet.iElement, sheet.material, sheet.name]);

  useEffect(() => {
    if (transparent === undefined) return;
    const origTransparent = sheet.material.transparent;
    sheet.material.transparent = transparent;

    return () => {
      sheet.material.transparent = origTransparent;
    };
  }, [sheet.material, transparent]);

  useEffect(() => {
    if (!side) return;
    const origSide = sheet.material.side;
    sheet.material.side = side;

    return () => {
      // Apply original side
      sheet.material.side = origSide;
    };
  }, [sheet.material, side]);

  useEffect(() => {
    if (backgroundTransparent === undefined) return;
    const origBackgroundTransparent = sheet.material.backgroundTransparent;
    sheet.material.backgroundTransparent = backgroundTransparent;

    return () => {
      // Apply original backgroundTransparent
      sheet.material.backgroundTransparent = origBackgroundTransparent;
    };
  }, [backgroundTransparent, sheet.material]);

  useEffect(() => {
    const origInvertLightness = sheet.material.invertLightness;
    const origSaturationMultiplier = sheet.material.saturationMultiplier;
    sheet.material.invertLightness = shouldInvertLightness;
    sheet.material.saturationMultiplier = shouldInvertLightness
      ? INVERTED_SHEET_SATURATION
      : 1;

    return () => {
      sheet.material.invertLightness = origInvertLightness;
      sheet.material.saturationMultiplier = origSaturationMultiplier;
    };
  }, [shouldInvertLightness, sheet.material]);

  if (sheet instanceof LodFloorPlan) {
    return (
      <LodSheetRenderer lod={sheet} iElement={sheet.iElement} {...events} />
    );
  }
  return (
    <SimpleSheetRenderer mesh={sheet} iElement={sheet.iElement} {...events} />
  );
}
